/**
 * Work we handle ourselves.
 *
 * Three switches on the job, all off by default (ADR 0102):
 *
 *   `materials_internal`    the material was already on the shelf and nothing
 *                           was ordered, so the "Material" step does not apply.
 *   `material_by_crew`      the crew supplies its own material — a different
 *                           fact, the same consequence. Either one is enough.
 *   `work_orders_internal`  the work is done in-house with no work order, so
 *                           the two work steps stop being maintained from the
 *                           work orders and are ticked by hand instead.
 *
 * The rules live here rather than in the checklist panel because the database
 * enforces the material ones by trigger and an agent can set any of them — the
 * UI is only one of the three things that has to agree about what they mean.
 */

/** Steps the material switch takes off the approved checklist. */
export const MATERIAL_STEPS = ["Material"] as const;

/** The two steps the work orders normally tick, and a person otherwise does. */
export const WORK_STEPS = ["All Work Scheduled", "All Work Complete"] as const;

export interface InternalHandling {
  materials_internal?: boolean | null;
  material_by_crew?: boolean | null;
  work_orders_internal?: boolean | null;
}

/**
 * Is there a material order to place on this job at all?
 *
 * Two switches answer no, and they are not exclusive — the crew brings the
 * shingles and we throw in the drip edge off our own shelf. The step comes back
 * only when both are off.
 */
export function noMaterialOrder(job: InternalHandling): boolean {
  return Boolean(job.materials_internal || job.material_by_crew);
}

/**
 * Steps hidden from the approved checklist by these switches.
 *
 * Only the material steps ever hide. The work steps stay on the list however
 * the work is handled: somebody still schedules Samuel or Carlos and still
 * says when they finished, and the ongoing-jobs board reads both to decide
 * which column the job is in (Jonathon, 2026-09-04).
 */
export function hiddenChecklistLabels(job: InternalHandling): string[] {
  return noMaterialOrder(job) ? [...MATERIAL_STEPS] : [];
}

/** Is this step one the switches take off the list? */
export function isHiddenChecklistLabel(
  job: InternalHandling,
  label: string,
): boolean {
  return hiddenChecklistLabels(job).includes(label);
}

/**
 * Are the two work steps ticked by a person rather than by the work orders?
 *
 * When they are, clicking one flips it instead of opening the Orders screen,
 * and the trade strip underneath has nothing to say — there are no work orders
 * and none are coming.
 */
export function workStepsAreManual(job: InternalHandling): boolean {
  return Boolean(job.work_orders_internal);
}
